import { useEffect, useMemo, useState } from "react";
import type { AiRrMatchResponse, Project } from "../services/api";
import { listProjects, matchResumeToProject, getRrMatchUploadUrl } from "../services/api";
import { PROJECT_STATUSES } from "../constants/projectStatus";
import { getErrorAlertMessage } from "../utils/errors";
import { Modal } from "./Modal";

const ACTIVE_PROJECT_STATUSES: readonly string[] = PROJECT_STATUSES.filter((status) => status !== "EXPIRED");
const MAX_FILE_SIZE_BYTES = 5_242_880; // 5 MB (matches backend limit)
const MIN_CHUNK_PROGRESS = 5;
const UPLOAD_TIMEOUT_MS = 90_000;

export function RRMatcher() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState("");
  const [resumeFilename, setResumeFilename] = useState<string | null>(null);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [alert, setAlert] = useState<{ type: "info" | "error"; message: string; details?: string[] } | null>(null);
  const [result, setResult] = useState<AiRrMatchResponse | null>(null);
  const [isMatching, setIsMatching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalStage, setModalStage] = useState<"upload" | "analyze" | null>(null);

  useEffect(() => {
    void loadProjects();
  }, []);

  async function loadProjects() {
    setLoadingProjects(true);
    setAlert(null);
    try {
      const items = await listProjects();
      // Only show OPEN RRs in the matcher
      const openProjects = items.filter(
        (project) => project.status && project.status.toUpperCase() === "OPEN",
      );
      setProjects(openProjects);
      if (openProjects.length === 0) {
        setAlert({ type: "info", message: "No open RR's available to match right now." });
      }
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("load open RR's", err) });
    } finally {
      setLoadingProjects(false);
    }
  }

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => (a.project_id > b.project_id ? 1 : -1));
  }, [projects]);

  function handleFileChange(fileList: FileList | null) {
    const [file] = fileList ?? [];
    if (!file) {
      setResumeFile(null);
      setResumeFilename(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setAlert({ type: "error", message: "That file is too large. Please choose a file under 5 MB." });
      return;
    }
    setResumeFile(file);
    setResumeFilename(file.name);
    setAlert(null);
  }

  async function handleMatch() {
    if (!selectedProjectId || (!resumeFile && !resumeText.trim())) {
      setAlert({ type: "error", message: "Select an RR and provide a resume file or text before running the matcher." });
      return;
    }
    setIsMatching(true);
    setShowModal(true);
    setModalStage(resumeFile ? "upload" : "analyze");
    setAlert(null);
    setResult(null);
    try {
      let s3Bucket: string | undefined;
      let s3Key: string | undefined;

      if (resumeFile) {
        setUploading(true);
        setUploadProgress(0);
        const presign = await getRrMatchUploadUrl({
          fileName: resumeFile.name,
          contentType: resumeFile.type,
          fileSize: resumeFile.size,
        });
        await uploadWithProgress(presign.upload_url, resumeFile, resumeFile.type || "application/octet-stream", (progress) => {
          const bounded = Math.max(progress, MIN_CHUNK_PROGRESS);
          setUploadProgress(bounded >= 100 ? 99 : bounded);
        });
        setUploading(false);
        setUploadProgress(null);
        s3Bucket = presign.bucket;
        s3Key = presign.key;
      }
      setModalStage("analyze");

      const response = await matchResumeToProject({
        projectId: selectedProjectId,
        resumeText: resumeText.trim() || undefined,
        notes: additionalNotes.trim() || undefined,
        s3Bucket,
        s3Key,
      });
      setResult(response);
      setShowModal(false);
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("match this resume to the RR", err) });
      setUploading(false);
      setUploadProgress(null);
      setShowModal(false);
    } finally {
      setIsMatching(false);
    }
  }

  const selectedProject = sortedProjects.find((project) => project.project_id === selectedProjectId);

  const resetAll = () => {
    setResult(null);
    setResumeText("");
    setResumeFilename(null);
    setResumeFile(null);
    setAdditionalNotes("");
    setAlert(null);
  };

  return (
    <div className="layout single-column">
      {alert && (
        <div className={`alert ${alert.type}`}>
          <p>{alert.message}</p>
          {alert.details && alert.details.length > 0 && (
            <ul>
              {alert.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!result && (
        <div className="panel">
          <h2>RR Matcher</h2>
          <p className="helper-text">
            Pick an active RR, upload a resume/CV, and optionally add context. We&apos;ll ask Claude 4.5 to rate the fit and summarize pros/cons.
          </p>

          <div className="form-grid">
            <label className="span-2">
              Active RR
              <select
                value={selectedProjectId}
                onChange={(event) => {
                  setSelectedProjectId(event.target.value);
                  setResult(null);
                }}
                disabled={loadingProjects || sortedProjects.length === 0}
              >
                <option value="">{loadingProjects ? "Loading RR's…" : "Select an RR"}</option>
                {sortedProjects.map((project) => (
                  <option key={project.project_id} value={project.project_id}>
                    {project.project_id} — {project.title}
                  </option>
                ))}
              </select>
            </label>

            {selectedProject && selectedProject.estimated_skills && selectedProject.estimated_skills.length > 0 && (
              <div className="panel muted" style={{ padding: "0.75rem", marginTop: "-0.25rem" }}>
                <div className="panel-header" style={{ marginBottom: "0.5rem" }}>
                  <div style={{ fontWeight: 600 }}>Skills in RR</div>
                </div>
                <div className="chip-row">
                  {selectedProject.estimated_skills.map((skill) => (
                    <span key={skill} className="chip">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <label className="span-2">
              Resume / CV (PDF, DOCX, DOC, RTF, TXT)
              <input
                type="file"
                accept=".pdf,.doc,.docx,.rtf,.txt,text/plain"
                onChange={(event) => void handleFileChange(event.target.files)}
              />
              {resumeFilename && <small className="helper-text">Loaded {resumeFilename}</small>}
            </label>

            <label className="span-2">
              Resume text (optional fallback)
              <textarea
                rows={3}
                value={resumeText}
                onChange={(event) => setResumeText(event.target.value)}
                placeholder="If you don't upload a file, paste the resume text here."
              />
            </label>

            <label className="span-2">
              Additional context (optional)
              <textarea
                rows={3}
                value={additionalNotes}
                onChange={(event) => setAdditionalNotes(event.target.value)}
                placeholder="Role nuances, must-have skills, visa/clearance needs…"
              />
            </label>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="primary"
              onClick={() => void handleMatch()}
              disabled={isMatching || uploading || !selectedProjectId || (!resumeFile && !resumeText.trim())}
            >
              {isMatching || uploading ? "Matching…" : "Run Matcher"}
            </button>
            <button type="button" className="ghost" onClick={resetAll} disabled={isMatching || uploading}>
              Reset
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="panel">
          <div className="panel-header">
            <h3>
              Fit Assessment{selectedProject ? ` · ${selectedProject.project_id}` : ""}
              {displayCandidateName(result.candidate_name, resumeFilename) ? ` · ${displayCandidateName(result.candidate_name, resumeFilename)}` : ""}
            </h3>
            {typeof result.match_score === "number" && <span className="chip">{result.match_score}% match</span>}
          </div>
          {result.summary && <p className="helper-text">{result.summary}</p>}
          {result.scorecard && (
            <div className="detail-block">
              <h4>Scorecard</h4>
              <div className="chip-row" style={{ flexWrap: "wrap" }}>
                <span className="chip">
                  Skills: {formatScore(result.scorecard.skills)}
                </span>
                <span className="chip">
                  Title: {formatScore(result.scorecard.title)}
                </span>
                <span className="chip">
                  Experience: {formatScore(result.scorecard.experience)}
                </span>
              </div>
            </div>
          )}
          <div className="detail-block">
            <h4>Pros</h4>
            {result.pros.length > 0 ? (
              <ul>
                {result.pros.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="muted">No strengths identified.</p>
            )}
          </div>
          <div className="detail-block">
            <h4>Cons / Risks</h4>
            {result.cons.length > 0 ? (
              <ul>
                {result.cons.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="muted">No risks noted.</p>
            )}
          </div>
          {result.model_id && <p className="muted">Model: {result.model_id}</p>}
          <div className="form-actions" style={{ marginTop: "1rem" }}>
            <button
              type="button"
              className="primary"
              onClick={() => {
                resetAll();
                setSelectedProjectId("");
              }}
            >
              Start again
            </button>
          </div>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => {
          if (!isMatching && !uploading) {
            setShowModal(false);
          }
        }}
        title={modalStage === "upload" ? "Uploading resume…" : "Analyzing…"}
      >
        {modalStage === "upload" && (
          <div className="progress">
            <div className="progress-bar" style={{ width: `${uploadProgress ?? 10}%` }} />
            <p style={{ marginTop: "0.5rem" }}>Uploading resume{uploadProgress ? ` (${uploadProgress}%)` : "…"}</p>
          </div>
        )}
        {modalStage === "analyze" && (
          <div style={{ textAlign: "center", padding: "0.5rem 0" }}>
            <div className="spinner" aria-label="Analyzing resume" />
            <p style={{ marginTop: "0.5rem" }}>Analyzing…</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function uploadWithProgress(url: string, file: File, contentType: string, onProgress?: (progress: number) => void) {
  if (typeof XMLHttpRequest === "undefined") {
    throw new Error("XMLHttpRequest is not available in this environment");
  }

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.send(file);
  });
}

function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `${value}%`;
}

function displayCandidateName(candidateName?: string | null, fallbackFilename?: string | null): string | null {
  if (candidateName && candidateName.trim()) {
    return candidateName.trim();
  }
  if (!fallbackFilename) return null;
  const base = fallbackFilename.replace(/\.[^/.]+$/, "");
  return base.replace(/[_-]+/g, " ").trim() || base;
}
